import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import FeatherIcon from 'feather-icons-react';
import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory, useParams, Redirect, Route, Switch } from 'react-router-dom';
import { Form, Row, Col, Spin, Breadcrumb } from 'antd';
import { useTranslation } from 'react-i18next';
import { AddUser } from './style';
import actions from '../../../redux/user/actions';
import { PageHeader } from '../../../components/page-headers/page-headers';
import { Cards } from '../../../components/cards/frame/cards-frame';
import { Main } from '../styled';
import {
  QUERY_RESOURCE_GROUP_FIND_ALL,
  QUERY_RESOURCE_GROUP_FIND_ONE,
  QUERY_USER_GUEST_FIND_ONE,
  QUERY_USER_RELATED_TENANT_UPDATE,
} from '../../../queries';
import { UserPermissions } from '../../../utils/enums/permissions.enum';
import { notificationError, notificationSuccess } from '../../../components/utilities/notification';
import { BreadcrumbWrapperStyle } from '../../../components/ui-elements/ui-elements-styled';
import { HomeOutlined } from '@ant-design/icons';

const {
  userFindOneBegin,
  userFindOneSuccess,
  userFindOneError,
  userUpdateBegin,
  userUpdateSuccess,
  userUpdateError,
} = actions;

const UserCreatePermissions = lazy(() => import('./UserCreatePermissions'));

const UserGuestEdit = ({ match }) => {
  const darkMode = useSelector(state => state.changeLayoutMode.data);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const userLoading = useSelector(state => state.user.loading);
  const user = useSelector(state => state.user.user);
  const currentLoading = useSelector(state => state.currentUserTenant.loading);
  const currentTenant = useSelector(state => state.currentUserTenant.currentTenant);
  const [submitted, setSubmitted] = useState(false);
  const [form] = Form.useForm();
  const [userRelatedTenantUpdate] = useMutation(QUERY_USER_RELATED_TENANT_UPDATE);
  const { userId } = useParams();
  const { data: dataResourceGroupFindAll } = useQuery(QUERY_RESOURCE_GROUP_FIND_ALL, {
    variables: {
      tenantId: currentTenant?.tenant._id,
    },
    fetchPolicy: 'network-only',
  });
  const [resourceGroupFindOne, { data: dataResourceGroupFindOne, loading: loadingResourceGroup }] = useLazyQuery(
    QUERY_RESOURCE_GROUP_FIND_ONE,
    {
      fetchPolicy: 'network-only',
    },
  );
  const { data, loading, error } = useQuery(QUERY_USER_GUEST_FIND_ONE, {
    variables: { id: userId || '' },
    fetchPolicy: 'network-only',
  });
  const [state, setState] = useState({
    form: {
      plataformaModelerLicense: false,
      userLicense: '',
      userPermissions: [],
      resourceGroup: null,
    },
  });
  const [permissions, setPermissions] = useState({
    bot: [],
    file: [],
    license: [],
    notification: [],
    tenant: [],
    transaction: [],
    user: [],
    workFlow: [],
  });

  /* eslint-disable */
  const validateMessages = {
    required: t('emptyField.1'),
    types: {
      email: t('type.email')
    }
  };
  /* eslint-enable */

  const userNotFound = useCallback(
    codeError => {
      notificationError(t(`codeResponse.${codeError}`));
      history.push(`/admin/user/`);
    },
    [history, t],
  );

  const updateForm = useCallback(
    userFound => {
      form.setFieldsValue({
        userLicense: userFound.relatedTenants[0].userLicense,
        resourceGroup: userFound.relatedTenants[0].resourceGroup.enabled ? userFound.relatedTenants[0].resourceGroup._id : null,
      });
      setState(x => ({
        form: {
          ...x.form,
          userLicense: userFound.relatedTenants[0].userLicense,
          userPermissions: userFound.relatedTenants[0].userPermissions,
          resourceGroup: userFound.relatedTenants[0].resourceGroup.enabled ? userFound.relatedTenants[0].resourceGroup : null,
          plataformaModelerLicense: userFound.relatedTenants[0].plataformaModelerLicense === 'Enterprise',
        },
      }));
      setPermissions({
        bot: userFound.relatedTenants[0].userPermissions?.filter(
          x => x.startsWith('Bot') && !x.startsWith('BotExecution'),
        ),
        file: userFound.relatedTenants[0].userPermissions?.filter(x => x.startsWith('File')),
        license: userFound.relatedTenants[0].userPermissions?.filter(x => x.startsWith('License')),
        notification: userFound.relatedTenants[0].userPermissions?.filter(
          x => x.startsWith('MailNotification') || x.startsWith('WhatsappNotification'),
        ),
        tenant: userFound.relatedTenants[0].userPermissions?.filter(x => x.startsWith('Tenant')),
        transaction: userFound.relatedTenants[0].userPermissions?.filter(
          x => x.startsWith('BotExecution') || x.startsWith('CustomReport') || x.startsWith('Realtime'),
        ),
        user: userFound.relatedTenants[0].userPermissions?.filter(x => x.startsWith('User')),
        workFlow: userFound.relatedTenants[0].userPermissions?.filter(x => x.startsWith('WorkFlow')),
      });
    },
    [form],
  );

  useEffect(() => {
    if (
      !currentLoading &&
      (!currentTenant?.userPermissions?.includes(UserPermissions.UserFindOne) ||
        !currentTenant?.userPermissions?.includes(UserPermissions.UserRelatedTenantUpdate))
    ) {
      history.push('/admin/user');
      notificationError(t(`codeResponse.403`));
    }

    dispatch(userFindOneBegin());

    if (!loading && data) {
      if (data.userGuestFindOne.success) {
        dispatch(userFindOneSuccess(data.userGuestFindOne.data));
        updateForm(data.userGuestFindOne.data);
      } else {
        dispatch(userFindOneError(error));
        userNotFound(data.userGuestFindOne.code);
      }
    }
  }, [dispatch, data, loading, error, currentTenant, currentLoading, history, t, updateForm, userNotFound]);

  const handleSubmit = useCallback(() => {
    dispatch(userUpdateBegin());

    userRelatedTenantUpdate({
      variables: {
        input: {
          email: user.email,
          relatedTenant: {
            tenant: currentTenant?.tenant._id,
            resourceGroup: state.form.resourceGroup._id,
            userLicense: state.form.userLicense,
            plataformaModelerLicense: state.form.plataformaModelerLicense ? 'Enterprise' : 'None',
            userPermissions: state.form.userPermissions,
          },
        },
      },
    })
      .then(({ data }) => {
        if (data.userRelatedTenantUpdate.success === true) {
          dispatch(userUpdateSuccess(data.userRelatedTenantUpdate.data));
          notificationSuccess(t('user.successUpdatedGuest'));
          history.push(`/admin/user/`);
        } else {
          dispatch(userUpdateError(data.userRelatedTenantUpdate.code));
          notificationError(t(`codeResponse.${data.userRelatedTenantUpdate.code}`));
        }
      })
      .catch(e => {
        console.log('User Guest Edit Error ', e);
        dispatch(userUpdateError());

        notificationError(t('codeResponse.UNEXPECTED_ERROR'));
      });
  }, [state.form, dispatch, history, t, currentTenant.tenant._id, user.email, userRelatedTenantUpdate]);

  const handleChangeSelect = e => {
    setState({
      form: {
        ...state.form,
        userLicense: e,
      },
    });
  };

  const handleChangeResourceGroup = id => {
    resourceGroupFindOne({
      variables: {
        id,
      },
    });
  };

  useEffect(() => {
    if (submitted) {
      handleSubmit();
      setSubmitted(false);
    }
  }, [submitted, handleSubmit]);

  useEffect(() => {
    if (dataResourceGroupFindOne?.resourceGroupFindOne.success) {
      const resourceGroupFound = dataResourceGroupFindOne?.resourceGroupFindOne;
      setState(x => ({
        form: {
          ...x.form,
          resourceGroup: resourceGroupFound.data,
          plataformaModelerLicense: false,
          userLicense: null,
        },
      }));
      form.setFieldsValue({
        userLicense: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataResourceGroupFindOne]);

  if (userLoading) {
    return (
      <div className="spin">
        <Spin />
      </div>
    );
  }
  return (
    <>
      <PageHeader ghost title={t('user.editGuest')} />
      <Main darkMode={darkMode}>
        <BreadcrumbWrapperStyle darkMode={darkMode}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <NavLink to={`/admin/`}>
                <HomeOutlined />
              </NavLink>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <NavLink to={`/admin/user`}>
                <span>{t('labels.backend.access.users.management')}</span>
              </NavLink>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <NavLink to="#">
                <NavLink to="#">{t('user.editGuest')}</NavLink>
              </NavLink>
            </Breadcrumb.Item>
          </Breadcrumb>
        </BreadcrumbWrapperStyle>
        <Row gutter={15}>
          <Col xs={24}>
            <AddUser>
              <Cards
                title={
                  <div>
                    <div className="card-nav">
                      <ul>
                        <li>
                          <NavLink to={`${match.url}/permissions`}>
                            <FeatherIcon icon="briefcase" size={14} />
                            {t('permissions')}
                          </NavLink>
                        </li>
                      </ul>
                    </div>
                  </div>
                }
              >
                <Switch>
                  <Suspense
                    fallback={
                      <div className="spin">
                        <Spin />
                      </div>
                    }
                  >
                    <Redirect exact from={`${match.url}`} to={`${match.url}/permissions`} />
                    <Route
                      exact
                      path={`${match.url}/permissions`}
                      render={() => (
                        <UserCreatePermissions
                          action="update"
                          resourceGroups={dataResourceGroupFindAll?.resourceGroupFindAll.data.filter(x => x.enabled)}
                          loadingResourceGroup={loadingResourceGroup}
                          form={form}
                          state={state}
                          userLoading={userLoading}
                          validateMessages={validateMessages}
                          permissions={permissions}
                          initialStateTenantDetail={data?.userGuestFindOne.data.relatedTenants[0]}
                          handleChangeSelect={handleChangeSelect}
                          setSubmitted={setSubmitted}
                          setState={setState}
                          setPermissions={setPermissions}
                          handleChangeResourceGroup={handleChangeResourceGroup}
                        />
                      )}
                    />
                  </Suspense>
                </Switch>
              </Cards>
            </AddUser>
          </Col>
        </Row>
      </Main>
    </>
  );
};

export default UserGuestEdit;
